import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { useEffect, useRef } from "react";
import { MapComponent } from "@/components/MapComponent";
import MapLayerControls from "./MapLayerControls";
import { getRootStore } from "@/stores/RootStore";
import { orderService } from "@/services/OrderService";
import { routePointsService } from "@/services/RoutePointsService";
import { supabase } from "@/utils/supabase";
import { MAP_COLORS } from "@/types/map";
import styles from "@/styles/Dashboard/DashboardMapBlock.module.less";
const DashboardMapBlock = observer(() => {
    const { dashboardStore, mapStore, basesStore, } = getRootStore();
    // Ref для отслеживания текущих ID техников (для cleanup)
    const observedTechniciansRef = useRef(new Set());
    // Generation counter: cancels stale async reaction handlers when selection changes rapidly
    const selectionGenerationRef = useRef(0);
    // === ОСНОВНАЯ ЛОГИКА: Реакция на выбор заказа/маршрута ===
    useEffect(() => {
        const disposer = reaction(() => ({
            orderId: dashboardStore.selectedOrderId,
            routeId: dashboardStore.selectedRouteId,
            selectionType: dashboardStore.selectionType,
            mapReady: mapStore.isInitialized,
        }), async ({ orderId, routeId, selectionType, mapReady }) => {
            if (!mapReady)
                return;
            // Increment generation: any stale in-flight handler will detect this and abort
            const myGeneration = ++selectionGenerationRef.current;
            // Очистить карту от предыдущего выбора (параллельно)
            await clearSelectionFromMap();
            // Abort if a newer selection was made while we were clearing
            if (selectionGenerationRef.current !== myGeneration)
                return;
            if (selectionType === 'order' && orderId) {
                await showOrderOnMap(orderId, myGeneration);
            }
            else if (selectionType === 'route' && routeId) {
                await showRouteOnMap(routeId, myGeneration);
            }
        }, { fireImmediately: true });
        return () => disposer();
    }, [dashboardStore, mapStore]);
    // === СЛОЙ ТЕХНИКОВ ===
    useEffect(() => {
        const disposer = reaction(() => ({
            visible: dashboardStore.mapLayerVisibility.technicians,
            mapReady: mapStore.isInitialized,
        }), async ({ visible, mapReady }) => {
            if (!mapReady)
                return;
            if (visible) {
                await showTechniciansOnMap();
            }
            else {
                await hideTechniciansFromMap();
            }
        }, { fireImmediately: true });
        return () => {
            disposer();
            // Cleanup: остановить все observers при размонтировании
            hideTechniciansFromMap();
        };
    }, [dashboardStore, mapStore]);
    // === СЛОЙ БАЗ ===
    useEffect(() => {
        const disposer = reaction(() => ({
            visible: dashboardStore.mapLayerVisibility.bases,
            mapReady: mapStore.isInitialized,
        }), async ({ visible, mapReady }) => {
            if (!mapReady)
                return;
            if (visible) {
                await showBasesOnMap();
            }
            else {
                await hideBasesFromMap();
            }
        }, { fireImmediately: true });
        return () => disposer();
    }, [dashboardStore, mapStore, basesStore]);
    // === HELPER FUNCTIONS ===
    /**
     * Очистить маркеры выбранного элемента с карты (параллельно для скорости)
     */
    const clearSelectionFromMap = async () => {
        // Snapshot перед началом удаления — безопасная итерация
        const locatorIds = mapStore.locators
            .filter((l) => l.id.startsWith('selected_'))
            .map((l) => l.id);
        const routeIds = mapStore.routes
            .filter((r) => r.id.startsWith('selected_'))
            .map((r) => r.id);
        // Параллельное удаление — быстрее и не блокирует на каждом await
        await Promise.all([
            ...locatorIds.map((id) => mapStore.removeLocator(id)),
            ...routeIds.map((id) => mapStore.removeRoute(id)),
        ]);
    };
    /**
     * Показать заказ на карте
     * @param generation - ID поколения для отмены устаревших вызовов
     */
    const showOrderOnMap = async (orderId, generation) => {
        try {
            const order = await orderService.getOrder(orderId);
            // Abort if selection changed while we were fetching
            if (selectionGenerationRef.current !== generation)
                return;
            if (!order?.addressInfo) {
                console.warn('Order has no address info:', orderId);
                return;
            }
            const latitude = order.addressInfo.getLatitude();
            const longitude = order.addressInfo.getLongitude();
            if (!latitude || !longitude) {
                console.warn('Order has no coordinates:', orderId);
                return;
            }
            const locator = {
                id: `selected_order_${orderId}`,
                position: {
                    latitude,
                    longitude,
                    address: order.addressInfo.getAddress(),
                },
                title: `Заказ #${order.orderNumber}`,
                description: order.addressInfo.getAddress(),
                icon: '📍',
                isVisible: true,
            };
            await mapStore.addLocator(locator);
            // Final generation check before centering
            if (selectionGenerationRef.current !== generation)
                return;
            // Центрировать карту на заказе (fitBounds с одной точкой = setCenter + setZoom)
            mapStore.fitBounds([{
                    latitude,
                    longitude,
                    address: order.addressInfo.getAddress(),
                }]);
        }
        catch (error) {
            console.error('Failed to show order on map:', error);
        }
    };
    /**
     * Показать маршрут на карте
     * @param generation - ID поколения для отмены устаревших вызовов
     */
    const showRouteOnMap = async (routeId, generation) => {
        try {
            // Загружаем точки маршрута через RoutePointsService
            const routePoints = await routePointsService.get(routeId);
            // Abort if selection changed while we were fetching
            if (selectionGenerationRef.current !== generation)
                return;
            if (!routePoints?.length) {
                console.warn('Route has no points:', routeId);
                return;
            }
            // Собрать точки для полилинии
            const mapPoints = routePoints
                .filter((point) => point.addressInfo?.getLatitude() && point.addressInfo?.getLongitude())
                .map((point, index) => ({
                latitude: point.addressInfo.getLatitude(),
                longitude: point.addressInfo.getLongitude(),
                address: point.addressInfo.getAddress(),
                orderIndex: index,
            }));
            if (mapPoints.length === 0) {
                console.warn('Route has no valid coordinates:', routeId);
                return;
            }
            // Добавить маршрут (полилинию) и маркеры параллельно
            const mapRoute = {
                id: `selected_route_${routeId}`,
                points: mapPoints,
                color: MAP_COLORS.PRIMARY_ROUTE,
                weight: 4,
                isVisible: true,
                title: `Маршрут #${routeId.slice(0, 8)}`,
            };
            const locators = routePoints
                .map((point, i) => {
                const lat = point.addressInfo?.getLatitude();
                const lng = point.addressInfo?.getLongitude();
                if (!lat || !lng)
                    return null;
                const isBase = !!point.idBase;
                const isCompleted = !!point.actualDepartureTime;
                return {
                    id: `selected_routepoint_${routeId}_${i}`,
                    position: {
                        latitude: lat,
                        longitude: lng,
                        address: point.addressInfo?.getAddress() || '',
                    },
                    title: isBase ? 'База' : `Точка ${i}`,
                    icon: isBase ? '🏠' : (isCompleted ? '✅' : '📍'),
                    isVisible: true,
                };
            })
                .filter(Boolean);
            // Параллельно добавляем маршрут и все маркеры
            await Promise.all([
                mapStore.addRoute(mapRoute),
                ...locators.map((locator) => mapStore.addLocator(locator)),
            ]);
            // Final generation check before centering
            if (selectionGenerationRef.current !== generation)
                return;
            // Подстроить bounds карты под все точки
            mapStore.fitBounds(mapPoints);
        }
        catch (error) {
            console.error('Failed to show route on map:', error);
        }
    };
    /**
     * Показать техников на карте
     */
    const showTechniciansOnMap = async () => {
        try {
            const { data, error } = await supabase()
                .from('technician_locations')
                .select('*, employees!inner(firstName, lastName)')
                .order('created_at', { ascending: false });
            if (error)
                throw error;
            // Добавить маркеры техников (дедупликация: берём только последнюю запись на техника)
            const seenTechnicians = new Set();
            for (const location of data || []) {
                if (seenTechnicians.has(location.technician_id))
                    continue;
                seenTechnicians.add(location.technician_id);
                const locator = {
                    id: `technician_${location.technician_id}`,
                    position: {
                        latitude: location.latitude,
                        longitude: location.longitude,
                        address: '',
                    },
                    title: `${location.employees?.firstName} ${location.employees?.lastName}`,
                    icon: '🧑‍🔧',
                    isVisible: true,
                };
                await mapStore.addLocator(locator);
                observedTechniciansRef.current.add(location.technician_id);
            }
        }
        catch (error) {
            console.error('Failed to load technician locations:', error);
        }
    };
    /**
     * Скрыть техников с карты
     */
    const hideTechniciansFromMap = async () => {
        // Удалить все маркеры техников
        const locators = mapStore.locators;
        for (const locator of locators) {
            if (locator.id.startsWith('technician_')) {
                await mapStore.removeLocator(locator.id);
            }
        }
        observedTechniciansRef.current.clear();
    };
    /**
     * Показать базы на карте
     */
    const showBasesOnMap = async () => {
        try {
            // Загрузить базы если не загружены
            if (basesStore.dataTable.data.length === 0) {
                await basesStore.dataTable.loadData();
            }
            const bases = basesStore.dataTable.data;
            for (const base of bases) {
                const lat = base.addressInfo?.getLatitude();
                const lng = base.addressInfo?.getLongitude();
                if (!lat || !lng)
                    continue;
                const locator = {
                    id: `base_${base.id}`,
                    position: {
                        latitude: lat,
                        longitude: lng,
                        address: base.addressInfo.getAddress(),
                    },
                    title: base.name,
                    description: base.addressInfo.getAddress(),
                    icon: '🏢',
                    isVisible: true,
                };
                await mapStore.addLocator(locator);
            }
        }
        catch (error) {
            console.error('Failed to show bases on map:', error);
        }
    };
    /**
     * Скрыть базы с карты
     */
    const hideBasesFromMap = async () => {
        const locators = mapStore.locators;
        for (const locator of locators) {
            if (locator.id.startsWith('base_')) {
                await mapStore.removeLocator(locator.id);
            }
        }
    };
    // === RENDER ===
    return (_jsxs("div", { className: styles.container, children: [_jsx(MapLayerControls, {}), _jsx("div", { className: styles.mapWrapper, children: _jsx(MapComponent, { width: "100%", height: "100%", config: {
                        center: {
                            latitude: 55.7558,
                            longitude: 37.6176,
                            address: 'Москва',
                        },
                        zoom: 10,
                    }, showLoadingSpinner: true, showErrorAlert: true, showCard: false }) }), dashboardStore.hasSelection && (_jsxs("div", { className: styles.selectionIndicator, children: [dashboardStore.selectionType === 'order' && (_jsx("span", { children: "\uD83D\uDCCD \u0417\u0430\u043A\u0430\u0437 \u0432\u044B\u0431\u0440\u0430\u043D" })), dashboardStore.selectionType === 'route' && (_jsx("span", { children: "\uD83D\uDEE3\uFE0F \u041C\u0430\u0440\u0448\u0440\u0443\u0442 \u0432\u044B\u0431\u0440\u0430\u043D" }))] }))] }));
});
DashboardMapBlock.displayName = "DashboardMapBlock";
export default DashboardMapBlock;
